import React from "react";
import ReactPDF from "@react-pdf/renderer";
import { formatCurrency } from "@utils/numbers";
import { Br, Sup, Text, View } from "./ReportObject";

const countForDataValue = (input) => {
    if(!input.length) {
        return 0;
    }

    // if it's a number, it's likely a multi-community report
    // TODO: figure out how to pass whether it's multi-community or not
    if(typeof input[0] === 'number') {
        return input.reduce((partialSum, a) => partialSum + a, 0);
    }

    return input.length;
};

const OverviewData = ({type, data, pdf = false}) => {

    console.log(type, data);
    if(typeof data === 'undefined') {
        return (<Text pdf={pdf}>(No Data)</Text>);
    }

    if (type === 'businesses_by_type') {
        return (<>
            {Object.values(data).map((row, rowIndex) => row.count === 0 ? null : (
                <View pdf={pdf} key={rowIndex} pdfStyles={{marginBottom: 4}}>
                    <Text pdf={pdf}>{`${row.business_type}`}</Text>
                    <Br pdf={pdf}/>
                    <Sup pdf={pdf}>{`${row.count} of ${row.total} (${row.percent}%)`}</Sup>
                </View>
            ))}
        </>);
    }

    if(type === 'businesses_relocated') {
        let inCount = 0;
        let outCount = 0;

        if(Array.isArray(data.in)) {
            inCount = data.in.length;
            outCount = data.out.length;
        } else {
            inCount = data.in;
            outCount = data.out;
        }

        return (<>
            <View pdf={pdf}><Text pdf={pdf}>In: {inCount}</Text></View>
            <View pdf={pdf}><Text pdf={pdf}>Out: {outCount}</Text></View>
        </>);
    }


    if (type === 'employee_changes' || type === 'total_employees') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>Full Time: {data.full_time}</Text></View>
            <View pdf={pdf}><Text pdf={pdf}>Part Time: {data.part_time}</Text></View>
            <View pdf={pdf}><Text pdf={pdf}>Seasonal: {data.seasonal}</Text></View>
        </>);
    }

    if (type === 'grants_received') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>{formatCurrency(data.total)}</Text></View>
            <View pdf={pdf}><Sup pdf={pdf}>{countForDataValue(data.grants)} {`${countForDataValue(data.grants) === 1 ? 'grant' : 'grants'}`} awarded</Sup></View>
        </>);
    }

    if (type === 'event_attendance') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>{countForDataValue(data.events)} {`${countForDataValue(data.events) === 1 ? 'event' : 'events'}`}</Text></View>
            <View pdf={pdf}><Sup pdf={pdf}>{data.total} {`${data.total === 1 ? 'person' : 'people'}`} attended</Sup></View>
        </>);
    }

    /*
    // handled by fallback
    if (type === 'events_held') {
        return <View pdf={pdf}><Text pdf={pdf}>{data.total}</Text></View>;
    }
    */

    if (type === 'parking') {
        const parkingTypes = [
            {type: 'public', name: 'Public'},
            {type: 'hourly', name: 'Hourly'},
            {type: 'handicapped', name: 'Accessible'},
            {type: 'leased', name: 'Leased'},
            {type: 'private', name: 'Private'},
        ];

        return (<>
            {parkingTypes.map((parkingType, parkingTypeIndex) => (<View pdf={pdf} key={parkingTypeIndex}>
                {(data[parkingType.type].lot > 0 || data[parkingType.type].on_street > 0) && <>
                    <Text pdf={pdf} bold>{parkingType.name}</Text>
                    <Br pdf={pdf}/>
                </>}
                {data[parkingType.type].lot > 0 && (<Text pdf={pdf}>{data[parkingType.type].lot} in lot</Text>)}
                {data[parkingType.type].lot > 0 && data[parkingType.type].on_street > 0 && <Br pdf={pdf}/>}
                {data[parkingType.type].on_street > 0 && (<Text pdf={pdf}>{data[parkingType.type].on_street} on street</Text>)}
            </View>))}
        </>);
    }

    if (type === 'public_improvements') {
        return (<View pdf={pdf}>
            <Text pdf={pdf}>{countForDataValue(data.improvements)}</Text>
            <Br pdf={pdf}/>
            <Sup pdf={pdf}>{formatCurrency(data.public_total)} Public</Sup>
            <Br pdf={pdf}/>
            <Sup pdf={pdf}>{formatCurrency(data.private_total)} Private</Sup>
        </View>);
    }

    if (type === 'public_investments') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>{formatCurrency(data.total)}</Text></View>
            <View pdf={pdf}><Sup pdf={pdf}>{countForDataValue(data.investments)} {`${countForDataValue(data.investments) === 1 ? 'investment' : 'investments'}`}</Sup></View>
        </>);
    }

    if (type === 'roi') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>${data.roi}:1</Text></View>
            <View pdf={pdf}><Text pdf={pdf}>Private: {formatCurrency(data.private)}</Text></View>
            <View pdf={pdf}><Text pdf={pdf}>Public: {formatCurrency(data.public)}</Text></View>
        </>);
    }

    if (type === 'assessed_market_value') {
        return <View pdf={pdf}><Text pdf={pdf}>{formatCurrency(data.total)}</Text></View>;
    }

    if (type === 'improved_properties') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>{formatCurrency(data.total)}</Text></View>
            <View pdf={pdf}><Sup pdf={pdf}>{countForDataValue(data.improvements)} {`${countForDataValue(data.improvements) === 1 ? 'improvement' : 'improvements'}`}</Sup></View>
        </>);
    }

    if (type === 'occupancy_rate') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>{Math.round(data.occupancy_rate * 100)}%</Text></View>
        </>);
    }

    if (type === 'properties_by_type') {
        return (<>
            {Object.values(data).map((row, rowIndex) => row.count === 0 ? null : (
                <View pdf={pdf} key={rowIndex} pdfStyles={{marginBottom: 4}}>
                    <Text pdf={pdf}>{`${row.property_type}`}</Text>
                    <Br pdf={pdf}/>
                    <Sup pdf={pdf}>{`${row.count} of ${row.total} (${row.percent}%)`}</Sup>
                </View>
            ))}
        </>);
    }

    if (type === 'rent_per_square_foot') {
        return <View pdf={pdf}><Text pdf={pdf}>{formatCurrency(data.rent)}</Text></View>;
    }

    if (type === 'sold_properties') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>{formatCurrency(data.total)}</Text></View>
            <View pdf={pdf}><Sup pdf={pdf}>{countForDataValue(data.sales)} {`${countForDataValue(data.sales) === 1 ? 'sale' : 'sales'}`}</Sup></View>
        </>);
    }

    if(type === 'transformational_strategies') {
        return <View pdf={pdf}><Text pdf={pdf}>{`${data.met} of ${data.total} (${data.rate * 100}%)`}</Text></View>;
    }

    if(type === 'staff_volunteer_hours') {
        return (<>
            <View pdf={pdf}><Text pdf={pdf}>Staff: {data.staff}</Text></View>
            <View pdf={pdf}><Text pdf={pdf}>Volunteer: {data.volunteer}</Text></View>
        </>);
    }

    if(data.hasOwnProperty('total')) {
        return <View pdf={pdf}><Text pdf={pdf}>{data.total}</Text></View>;
    }

    if(typeof data === 'number') {
        return <View pdf={pdf}><Text pdf={pdf}>{data}</Text></View>;
    }

    // Fallback
    return (<Text pdf={pdf}>(Not Implemented)</Text>);
}

export default OverviewData;
